import React, { createContext, useContext, useState, ReactNode } from "react";
import en from "../locales/en.json";
import es from "../locales/es.json";
import {
  Translations,
  LanguageContextType,
} from "../interfaces/LanguageContextTypes"; // Import interfaces

const translations: Record<string, Translations> = { en, es };

// Create the context with a default value
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<string>("en");

  const translate = (
    key: keyof Translations | `${keyof Translations}.${string}`
  ): string => {
    const keys = key.split("."); // Split the key by dot for nested access
    const translation: Translations = keys.reduce<Translations>(
      (acc, curr) => {
        if (typeof acc === "object" && acc !== null) {
          return acc[curr as keyof Translations] as unknown as Translations; // Cast to unknown first
        }
        return acc; // Return acc if it's not an object
      },
      translations[language] as Translations // Ensure initial value is of type Translations
    );
    return (translation as unknown) !== undefined
      ? (translation as unknown as string)
      : key; // Return the translation or the key if not found
  };

  return (
    <LanguageContext.Provider value={{ translate, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Add this line to ensure the file is treated as a module
export {};
